import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';  // ✅ Only these imports needed
import { auth, db } from '../firebase';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Personal.css';
import logo from '../components/assets/logo.png';

const Personal = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false); // ✅ No need to check auth

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(prev => !prev);
    } else {
      setShowConfirmPassword(prev => !prev);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email.trim(), 
        formData.password
      );

      // Create basic photographer profile
      const photographerData = {
        uid: userCredential.user.uid,
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        profileImage: `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.fullName.trim())}&background=6366f1&color=fff&size=128&bold=true`,
        phoneNumber: '',
        description: 'Professional photographer ready to capture your moments!',
        emailVerified: false,
        profileComplete: 25,
        portfolioVisible: false,
        createdAt: new Date(),
        serviceAreas: [],
        serviceCategories: {}
      };

      // Save to photographers collection
      await setDoc(doc(db, 'photographers', userCredential.user.uid), photographerData);

      // Save login data
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userId', userCredential.user.uid);
      localStorage.setItem('userEmail', formData.email.trim());
      localStorage.setItem('userName', formData.fullName.trim());
      localStorage.setItem('userType', 'photographer');

      toast.success('Account created successfully! Redirecting to your portfolio...', { autoClose: 2000 });
      
      setTimeout(() => {
        navigate('/Upload');
      }, 2000);

    } catch (error) {
      console.error('Error creating account:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: 'An account already exists with this email address.' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ password: 'Password is too weak. Please use at least 6 characters.' });
      } else {
        toast.error('Error creating account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="personal-page-body">
      <div className="personal-container">
        {/* Editorial panel */}
        <div className="personal-left">
          <img src="/images/join-photographer.jpg" alt="" />
          {/* <img src={logo} alt="LitStudio" className="personal-logo" /> */}

          <button
            type="button"
            className="personal-back-button"
            onClick={() => navigate('/login')}
          >
            <svg className="personal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
            </svg>
            Back to login
          </button>

          <div>
            <div className="personal-caption">Turn your portfolio into bookings.</div>
            <p className="personal-caption-sub">
              Set your own packages, manage your schedule in one place, and get
              paid securely on every completed shoot.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <div className="personal-right">
          <div className="personal-panel">
            <span className="personal-badge">
              <svg className="personal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
              Photographer
            </span>

            <h2>Join LitStudio</h2>
            <p className="personal-subtitle">
              Already have an account?{" "}
              <button type="button" className="personal-link-inline" onClick={() => navigate('/login')}>
                Log in
              </button>
            </p>

            <form onSubmit={handleSubmit} className="personal-form">
              <div className="personal-form-group">
                <label htmlFor="fullName">Full name <span className="personal-required">*</span></label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="Your full name"
                  autoComplete="name"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`personal-input-field ${errors.fullName ? 'error' : ''}`}
                  required
                />
                {errors.fullName && <span className="personal-field-error">{errors.fullName}</span>}
              </div>

              <div className="personal-form-group">
                <label htmlFor="email">Email address <span className="personal-required">*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@studio.com"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`personal-input-field ${errors.email ? 'error' : ''}`}
                  required
                />
                {errors.email && <span className="personal-field-error">{errors.email}</span>}
              </div>

              <div className="personal-form-group">
                <label htmlFor="password">Password <span className="personal-required">*</span></label>
                <div className="personal-password-wrap">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`personal-input-field ${errors.password ? 'error' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    className="personal-password-toggle"
                    onClick={() => togglePasswordVisibility('password')}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="personal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                      </svg>
                    ) : (
                      <svg className="personal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <span className="personal-field-error">{errors.password}</span>}
              </div>

              <div className="personal-form-group">
                <label htmlFor="confirmPassword">Confirm password <span className="personal-required">*</span></label>
                <div className="personal-password-wrap">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`personal-input-field ${errors.confirmPassword ? 'error' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    className="personal-password-toggle"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg className="personal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20C5 20 1 12 1 12a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                      </svg>
                    ) : (
                      <svg className="personal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <span className="personal-field-error">{errors.confirmPassword}</span>}
              </div>

              <button
                className="personal-submit-btn"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="personal-icon personal-icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
                    </svg>
                    Creating account...
                  </>
                ) : (
                  "Create photographer account"
                )}
              </button>
            </form>

            <p className="personal-terms">
              Looking to book a photographer instead?{" "}
              <button type="button" onClick={() => navigate('/CreateAccount')}>Sign up as a client</button>
            </p>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={4000} hideProgressBar newestOnTop closeOnClick pauseOnHover theme="light" />
    </div>
  );
};

export default Personal;
